import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Archive, ArrowRight, Loader2, MapPin, Pencil, Phone, Trash2 } from "lucide-react";
import axiosInstance from "../utils/axiosInstance";
import { formatNumber } from "../utils/numberFormat";

const StorageDetails = () => {
  const { storageId } = useParams();
  const navigate = useNavigate();
  const [storage, setStorage] = useState(null);
  const [goods, setGoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const [storageRes, goodsRes] = await Promise.all([
          axiosInstance.get(`/storages/${storageId}/`),
          axiosInstance.get("/cottage-goods/", {
            params: { storage: storageId, page_size: 500 },
          }),
        ]);
        setStorage(storageRes.data);
        setGoods(Array.isArray(goodsRes.data.results) ? goodsRes.data.results : []);
      } catch (err) {
        setError("خطا در دریافت اطلاعات انبار");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [storageId]);

  const handleDelete = async () => {
    if (!window.confirm("آیا مطمئن هستید؟")) return;
    await axiosInstance.delete(`/storages/${storageId}/`);
    navigate("/storages");
  };

  if (loading) {
    return (
      <div className="mt-24 flex justify-center text-slate-600" dir="rtl">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!storage) {
    return (
      <div className="mt-24 px-4 font-vazirmatn" dir="rtl">
        <div className="mx-auto max-w-3xl rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
          {error || "انبار یافت نشد."}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-24 px-3 sm:px-6 font-vazirmatn" dir="rtl">
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-lg overflow-hidden">
          <div className="px-5 sm:px-7 py-5 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/storages"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  <ArrowRight size={18} />
                  بازگشت
                </Link>
                <Link
                  to={`/storages/${storage.id}/edit`}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white hover:opacity-95"
                >
                  <Pencil size={18} />
                  ویرایش
                </Link>
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-bold text-rose-700 hover:bg-rose-100"
                >
                  <Trash2 size={18} />
                  حذف
                </button>
              </div>

              <div className="flex items-center justify-end gap-3 text-right">
                <div>
                  <h1 className="text-2xl font-extrabold text-slate-900">
                    {storage.storage_name}
                  </h1>
                  <p className="mt-1 text-sm text-slate-500">
                    جزئیات انبار و کالاهای اختصاص یافته
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Archive size={24} />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5 sm:p-7">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Archive size={16} />
                تعداد کالا
              </div>
              <div className="mt-1 text-2xl font-extrabold text-slate-900">
                {formatNumber(storage.goods_count || goods.length)}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Phone size={16} />
                تلفن
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {storage.storage_phone || "-"}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <MapPin size={16} />
                آدرس
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {storage.storage_address || "-"}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-slate-200 px-5 py-4 bg-slate-50">
            <h2 className="text-base font-extrabold text-slate-900 text-right">
              کالاهای این انبار
            </h2>
          </div>
          <div className="p-5">
            {goods.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                کالایی برای این انبار ثبت نشده است.
              </div>
            ) : (
              <div className="overflow-auto rounded-2xl border border-slate-200">
                <table className="min-w-full text-sm bg-white">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr>
                      <th className="p-3 text-right">کد کالا</th>
                      <th className="p-3 text-right">کوتاژ</th>
                      <th className="p-3 text-right">شرح</th>
                      <th className="p-3 text-right">تعداد</th>
                      <th className="p-3 text-right">وزن خالص</th>
                      <th className="p-3 text-right">وزن ناخالص</th>
                      <th className="p-3 text-right">وضعیت فروش</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {goods.map((good) => (
                      <tr key={good.id} className="hover:bg-slate-50">
                        <td className="p-3 font-semibold">
                          <Link to={`/storage/goods/${good.id}`} className="text-blue-700 hover:underline">
                            {good.goodscode || "-"}
                          </Link>
                        </td>
                        <td className="p-3">
                          {good.cottage?.cottage_number ? (
                            <Link to={`/cottages/${good.cottage.cottage_number}`} className="text-blue-700 hover:underline">
                              {good.cottage.cottage_number}
                            </Link>
                          ) : "-"}
                        </td>
                        <td className="p-3">{good.goods_description || "-"}</td>
                        <td className="p-3">{formatNumber(good.quantity)}</td>
                        <td className="p-3">{good.netweight == null ? "-" : formatNumber(good.netweight, { maximumFractionDigits: 3 })}</td>
                        <td className="p-3">{good.grossweight == null ? "-" : formatNumber(good.grossweight, { maximumFractionDigits: 3 })}</td>
                        <td className="p-3">
                          {good.is_sold ? (
                            good.sold_to_customer_detail ? (
                              <Link to={`/customers/details/${good.sold_to_customer_detail.id}`} className="text-blue-700 hover:underline">
                                {good.sold_to_customer_detail.full_name}
                              </Link>
                            ) : "فروخته‌شده"
                          ) : "فروخته‌نشده"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageDetails;
